import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "../../components/buttons/button";
import Dropdown from "../../components/dropdown/dropdown";
import { EmptyEventState } from "../../components/empty/emptyEventState";
import { FullEventCard } from "../../components/event/fullEventCard";
import { Icon } from "../../components/icon/icon";
import { EventCardsLoader } from "../../components/loaders/loaders";
import { SentinelDiv } from "../../components/sentinelDiv";
import { TabNav } from "../../components/tabNav";
import { PageHeader } from "../../components/typography/pageHeader";
import { useGetEventsQuery } from "../../redux/features/event/event.api";
import {
  CreatedEvent,
  EventRole,
  EventStatus,
} from "../../redux/features/event/event.types";
import { useAppSelector } from "../../redux/hooks";
import { QueryParams } from "../../utils/types";

export const EventsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const lastPage = useRef(0);
  const [eventsList, setEventsList] = useState<CreatedEvent[]>([]);
  const [query, setQuery] = useState<
    QueryParams & {
      EventStatus?: EventStatus;
      role: EventRole | "";
    }
  >({
    EventStatus: undefined,
    role: "",
    page: 1,
    limit: 10,
  });

  const { hasCreatedEvent } = useAppSelector((state) => state.auth);

  const navigate = useNavigate();

  const {
    data: events,
    isFetching,
    isUninitialized,
  } = useGetEventsQuery(
    {
      status: query.EventStatus,
      type: [null, ""].includes(query.role)
        ? undefined
        : (query.role as EventRole),
      page: query.page,
      limit: query.limit,
    },
    {
      refetchOnMountOrArgChange: true,
      skip: !query.EventStatus,
    },
  );

  const tabs = [
    {
      title: "Upcoming",
      to: "upcoming",
      onClick: (selected: string) => {
        setSearchParams({
          ...Object.fromEntries(searchParams.entries()),
          EventStatus: selected as EventStatus,
        });
      },
    },
    {
      title: "Past",
      to: "completed",
      onClick: (selected: string) => {
        setSearchParams({
          ...Object.fromEntries(searchParams.entries()),
          EventStatus: selected as EventStatus,
        });
      },
    },
    {
      title: "Draft",
      to: "draft",
      onClick: (selected: string) => {
        setSearchParams({
          ...Object.fromEntries(searchParams.entries()),
          EventStatus: selected as EventStatus,
        });
      },
    },
  ];

  useEffect(() => {
    const q: QueryParams & {
      EventStatus: EventStatus;
      role: EventRole | "";
    } = JSON.parse(JSON.stringify(query));
    setEventsList([]);

    q.EventStatus = (searchParams.get("EventStatus") ??
      "upcoming") as EventStatus;

    q.role = searchParams.get("filter") as EventRole;

    q.page = 1;

    lastPage.current = 0;
    setQuery(q);
    // eslint-disable-next-line
  }, [searchParams]);

  useEffect(() => {
    if (events?.data?.data && query.page! > lastPage.current) {
      setEventsList((prev) => [...prev, ...events.data.data]);
      lastPage.current = query.page!;
    }
    // eslint-disable-next-line
  }, [events?.data.data]);

  return (
    <div className="w-full">
      <div className="flex w-full items-center justify-between gap-4">
        <PageHeader header={"Events"} />

        <Button
          text="Create Event"
          icon="plus"
          type="button"
          className="hidden max-w-fit md:flex"
          onClick={() => {
            if (hasCreatedEvent) {
              navigate("/event/new");
            } else {
              navigate("/complete-profile");
            }
          }}
        />
      </div>

      <div className="mt-8 flex flex-col gap-4">
        <div className="flex w-full flex-wrap items-center justify-between gap-2">
          <TabNav
            navs={tabs}
            className="!min-w-full md:!min-w-fit"
            tabClassName=""
            activeTab={query.EventStatus!}
            variant="switcher"
            theme="plain"
          />

          <div className="flex w-full items-center justify-end gap-2 md:w-fit">
            <p className="text-sm text-grey-900">Filter</p>

            <Dropdown
              menuItems={[
                {
                  name: "All",
                  value: "all",
                  onClick: () => {
                    setSearchParams({
                      ...Object.fromEntries(searchParams.entries()),
                      filter: "",
                    });
                  },
                },
                {
                  name: "Guest",
                  value: "guest",
                  onClick: () => {
                    setSearchParams({
                      ...Object.fromEntries(searchParams.entries()),
                      filter: "guest",
                    });
                  },
                },
                {
                  name: "Organizer",
                  value: "organiser",
                  onClick: () => {
                    setSearchParams({
                      ...Object.fromEntries(searchParams.entries()),
                      filter: "organiser",
                    });
                  },
                },
              ]}
              menuClassName="!min-w-[150px] !shadow-def-cust !border-none"
              position="bottom end"
            >
              <button
                type="button"
                className="shadow-def-cust flex w-fit items-center gap-[2px] rounded-full bg-white px-4 py-[6px]"
              >
                <span className="tetx-grey-900 text-sm font-bold capitalize">
                  {query.role || "All"}
                </span>
                <Icon
                  svg="caret-down"
                  width={16}
                  height={16}
                  className="text-grey-400"
                />
              </button>
            </Dropdown>
          </div>
        </div>

        {!isFetching && [undefined, 0].includes(events?.data?.totalCount) ? (
          <EmptyEventState eventStatus={query.EventStatus} />
        ) : undefined}

        {eventsList.length > 0 ? (
          <div className="flex flex-col gap-3">
            {eventsList.map((event, idx) => {
              return (
                <FullEventCard event={event} key={`${event._id}-${idx}`} />
              );
            })}
          </div>
        ) : undefined}

        {isFetching ? <EventCardsLoader numberOfCards={4} /> : undefined}
      </div>

      <SentinelDiv
        onVisible={() => {
          if (
            query.page! <
              Math.ceil((events?.data?.totalCount ?? 0) / query.limit!) &&
            !isFetching &&
            !isUninitialized
          ) {
            setQuery({
              ...query,
              page: query.page! + 1,
            });
          }
        }}
        loading={false}
        className="w-full text-center text-grey-300 opacity-0"
      />
    </div>
  );
};
